import { useNavigate, useLocation, useMatch } from 'react-router-dom';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { Icon, type IconName } from './icons.js';
import type { JobStatus, Tech } from '../data/seed.js';

// ---------- Nav config ----------
interface NavEntry {
  path: string;
  label: string;
  icon: IconName;
}

const NAV: NavEntry[] = [
  { path: '/', label: 'Dashboard', icon: 'home' },
  { path: '/clients', label: 'Clients', icon: 'users' },
  { path: '/jobs', label: 'Jobs', icon: 'wrench' },
  { path: '/calendar', label: 'Calendar', icon: 'calendar' },
];

function isActive(routePath: string, navPath: string): boolean {
  if (navPath === '/') return routePath === '/' || routePath === '';
  return routePath === navPath || routePath.startsWith(navPath + '/');
}

// ---------- Sidebar (desktop) ----------
export function Sidebar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <div className="brand-mark">W</div>
        <div>
          <div className="brand-name">WalnutFix</div>
          <div className="brand-sub">CRM · Allen TX</div>
        </div>
      </div>
      <nav className="sidebar-nav">
        <div className="sidebar-nav-section">Workspace</div>
        {NAV.map((n) => (
          <button
            key={n.path}
            className={'nav-item ' + (isActive(pathname, n.path) ? 'active' : '')}
            onClick={() => navigate(n.path)}
          >
            {Icon[n.icon]({ size: 16 })}
            <span>{n.label}</span>
            {n.path === '/jobs' ? <span className="nav-badge">4</span> : null}
          </button>
        ))}
      </nav>
      <div className="sidebar-footer">
        <div
          className="avatar"
          style={{ background: 'oklch(0.6 0.13 30 / 0.18)', color: 'oklch(0.45 0.13 30)' }}
        >
          LN
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Lin Nguyen</div>
          <div style={{ fontSize: 11.5, color: 'hsl(var(--muted-foreground))' }}>Owner · WalnutFix</div>
        </div>
      </div>
    </aside>
  );
}

// ---------- Mobile top bar ----------
interface TopBarProps {
  title: string;
  back: string | null;
  onMenu: () => void;
}

export function TopBar({ title, back, onMenu }: TopBarProps) {
  const navigate = useNavigate();
  return (
    <div className="topbar">
      {back ? (
        <button className="icon-btn" onClick={() => navigate(back)} aria-label="Back">
          {Icon.arrowLeft({ size: 16 })}
        </button>
      ) : (
        <button className="icon-btn" onClick={onMenu} aria-label="Menu">
          {Icon.menu({ size: 16 })}
        </button>
      )}
      <div className="topbar-title">{title}</div>
      <button className="icon-btn" aria-label="Notifications">
        {Icon.bell({ size: 16 })}
      </button>
    </div>
  );
}

// ---------- Mobile bottom tabs ----------
export function BottomNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (
    <nav className="bottom-nav" role="navigation" aria-label="Primary">
      {NAV.map((n) => (
        <button
          key={n.path}
          className={'bn-item ' + (isActive(pathname, n.path) ? 'active' : '')}
          onClick={() => navigate(n.path)}
        >
          {Icon[n.icon]({ size: 20 })}
          <span>{n.label}</span>
        </button>
      ))}
    </nav>
  );
}

// ---------- Mobile menu drawer ----------
interface DrawerProps {
  open: boolean;
  onClose: () => void;
}

export function MobileDrawer({ open, onClose }: DrawerProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 60,
        display: 'flex',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 260,
          background: 'hsl(var(--card))',
          borderRight: '1px solid hsl(var(--border))',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className="sidebar-brand">
          <div className="brand-mark">W</div>
          <div>
            <div className="brand-name">WalnutFix</div>
            <div className="brand-sub">CRM · Allen TX</div>
          </div>
        </div>
        <div className="sidebar-nav">
          <div className="sidebar-nav-section">Workspace</div>
          {NAV.map((n) => (
            <button
              key={n.path}
              className={'nav-item ' + (isActive(pathname, n.path) ? 'active' : '')}
              onClick={() => {
                navigate(n.path);
                onClose();
              }}
            >
              {Icon[n.icon]({ size: 16 })}
              <span>{n.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Page title resolver ----------
export function usePageTitle(): { title: string; back: string | null } {
  const { pathname } = useLocation();
  const matchClient = useMatch('/clients/:id');
  const matchJob = useMatch('/jobs/:id');
  if (pathname === '/' || pathname === '') return { title: 'Dashboard', back: null };
  if (matchClient) return { title: 'Client', back: '/clients' };
  if (matchJob) return { title: 'Job', back: '/jobs' };
  if (pathname === '/clients') return { title: 'Clients', back: null };
  if (pathname === '/jobs') return { title: 'Jobs', back: null };
  if (pathname === '/calendar') return { title: 'Calendar', back: null };
  return { title: 'WalnutFix', back: null };
}

// ---------- Pills / status helpers ----------
const STATUS_CLASS: Record<JobStatus, string> = {
  New: 'new',
  Scheduled: 'scheduled',
  'In Progress': 'progress',
  'Waiting Parts': 'waiting',
  Completed: 'completed',
  'Follow-up': 'followup',
  Cancelled: 'cancel',
};

export function StatusPill({ status }: { status: JobStatus }) {
  return (
    <span className={'pill ' + (STATUS_CLASS[status] || '')}>
      <span className="dot" />
      {status}
    </span>
  );
}

const INVOICE_PILL: Record<string, string> = {
  Paid: 'completed',
  Sent: 'scheduled',
  Overdue: 'cancel',
  Draft: 'waiting',
};
export function InvoicePill({ status }: { status: string }) {
  return (
    <span className={'pill ' + (INVOICE_PILL[status] || '')}>
      <span className="dot" />
      {status}
    </span>
  );
}

const ESTIMATE_PILL: Record<string, string> = {
  Draft: 'waiting',
  Sent: 'scheduled',
  Approved: 'completed',
  Declined: 'cancel',
  Converted: 'followup',
};
export function EstimatePill({ status }: { status: string }) {
  return (
    <span className={'pill ' + (ESTIMATE_PILL[status] || '')}>
      <span className="dot" />
      {status}
    </span>
  );
}

// ---------- Avatar w/ tech color ----------
type AvatarSize = 'sm' | 'md' | 'lg';
export function TechAvatar({ tech, size = 'md' }: { tech?: Tech | null; size?: AvatarSize }) {
  if (!tech) return <div className={'avatar ' + (size === 'sm' ? 'sm' : '')}>—</div>;
  const sz = size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : '';
  return (
    <div
      className={'avatar ' + sz}
      style={{ background: tech.color + '24', color: tech.color, fontWeight: 600 }}
      title={tech.name}
    >
      {tech.initials}
    </div>
  );
}

// ---------- Format helpers ----------
export const fmtDate = (iso: string, opts: Intl.DateTimeFormatOptions = {}): string => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...opts });
};

export const fmtTime = (iso: string): string => {
  const d = new Date(iso);
  return d
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    .replace(/\s/g, '')
    .toLowerCase();
};

export const fmtMoney = (n: number): string =>
  '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ---------- Layout wrapper ----------
export function AppLayout({ children }: { children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { title, back } = usePageTitle();
  return (
    <div className="app">
      <Sidebar />
      <main className="main">
        <TopBar title={title} back={back} onMenu={() => setDrawerOpen(true)} />
        {children}
      </main>
      <BottomNav />
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  );
}

// re-export common style helper for inline styles
export type { CSSProperties };
